import { forwardRef, useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Radio,
  FormGroup,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import FormikForm from 'src/ui-component/FormikFormLibrary';
import { Fields } from 'src/ui-component/FormikFormLibrary/FormikTypes';
import { useDispatch } from 'react-redux';
import { updateFormData } from 'src/redux-store/slices/foodAndBeverageFormSlice';

// Define interfaces for the new structure
export interface SpecialEventData {
  status: boolean;
  eventParticipationType: string;
  eventAddress: string;
  eventDateFrom: string;
  eventDateTo: string;
  rainDateFrom: string;
  rainDateTo: string;
}

export interface EstablishmentTypeData {
  [key: string]: boolean | number | string | null;
  id: number | null;
  size: string;
  fee: number | null;
}

export interface EstablishmentFormData {
  [EstablishmentType: string]: EstablishmentTypeData | SpecialEventData;
}

// Size options for reference
export interface SizeOption {
  id: number;
  size: string;
  description: string;
  fee: number;
}

export interface FeeStructure {
  [establishmentType: string]: SizeOption[];
}

// Default fee structure values for reference
export const DEFAULT_FEE_STRUCTURE: FeeStructure = {
  'Restaurant': [
    { 
      id: 1, 
      size: 'Small', 
      description: '1-25 seats', 
      fee: 125
    },
    { 
      id: 2, 
      size: 'Medium', 
      description: '26-75 seats', 
      fee: 250
    },
    { 
      id: 3, 
      size: 'Large', 
      description: '76+ seats', 
      fee: 375
    }
  ],
  'RetailFoodEstablishment': [
    { 
      id: 1, 
      size: 'Small', 
      description: 'Under 1,000 sq ft', 
      fee: 100
    },
    { 
      id: 2, 
      size: 'Medium', 
      description: '1,000-5,000 sq ft', 
      fee: 200
    },
    { 
      id: 3, 
      size: 'Large', 
      description: 'Over 5,000 sq ft', 
      fee: 300
    }
  ],
  'MobileVehicle': [
    { 
      id: 1, 
      size: 'Food Truck', 
      description: 'Mobile food vehicle', 
      fee: 150 
    },
    { 
      id: 2, 
      size: 'Cart', 
      description: 'Small mobile unit', 
      fee: 75
    },
    { 
      id: 3, 
      size: 'Trailer', 
      description: 'Towed food establishment', 
      fee: 200
    }
  ],
  'Special Event': []
};

// Default establishment form data
export const DEFAULT_ESTABLISHMENT_FORM_DATA: EstablishmentFormData = {
  'Restaurant': {
    'Restaurant': false,
    id: null,
    size: '',
    fee: null
  },
  'RetailFoodEstablishment': {
    'RetailFoodEstablishment': false,
    id: null,
    size: '',
    fee: null
  },
  'MobileVehicle': {
    'MobileVehicle': false,
    id: null,
    size: '',
    fee: null
  },
  'Special Event': {
    status: false,
    eventParticipationType: '',
    eventAddress: '',
    eventDateFrom: '',
    eventDateTo: '',
    rainDateFrom: '',
    rainDateTo: ''
  }
};

interface EstablishmentTypeProps {
  formData?: EstablishmentFormData;
  handleSubmit?: (val: EstablishmentFormData) => void;
  setFieldValue?: (field: string, value: unknown) => void;
  isButtonHide?: boolean;
  setIsButtonHide?: (flag: boolean) => void;
  isEdit?: boolean;
  
  // Additional props if passed directly
  establishmentData?: EstablishmentFormData;
  handleEstablishmentTypeChange?: (type: string, checked: boolean) => void;
  handleSizeSelection?: (type: string, sizeId: number, size: string, fee: number) => void;
  handleSpecialEventChange?: (fieldName: string, value: unknown) => void;
  errors?: Record<string, unknown>;
  touched?: Record<string, unknown>;
}

const EstablishmentType = forwardRef<HTMLDivElement, EstablishmentTypeProps>(
  ({
    formData,
    handleSubmit,
    setFieldValue,
    isButtonHide,
    // New props with updated naming
    establishmentData: propEstablishmentData,
    handleEstablishmentTypeChange: propHandleEstablishmentTypeChange,
    handleSizeSelection: propHandleSizeSelection,
    handleSpecialEventChange: propHandleSpecialEventChange
  }, ref) => {
    const dispatch = useDispatch();
    const [feeStructure] = useState<FeeStructure>(DEFAULT_FEE_STRUCTURE);
    
    // Initialize form values from props or defaults - with improved safety check
    const [formValues, setFormValues] = useState<EstablishmentFormData>(() => {
      // Start with default structure to ensure all keys exist
      const baseFormValues = {...DEFAULT_ESTABLISHMENT_FORM_DATA};
      
      // Then merge with any provided data
      if (propEstablishmentData && Object.keys(propEstablishmentData).length > 0) {
        return {...baseFormValues, ...propEstablishmentData};
      } else if (formData && Object.keys(formData).length > 0) {
        return {...baseFormValues, ...formData};
      }
      
      return baseFormValues;
    });
    
    // Debug logging
    useEffect(() => {}, [formValues, feeStructure]);
    
    // Update state when props change - with improved safety checks
    useEffect(() => {
      // Start with the default structure to ensure we have all required keys
      const baseFormValues = {...DEFAULT_ESTABLISHMENT_FORM_DATA};
      
      // Then merge with any provided data
      if (propEstablishmentData && Object.keys(propEstablishmentData).length > 0) {
        setFormValues({...baseFormValues, ...propEstablishmentData});
      } else if (formData && Object.keys(formData).length > 0) {
        setFormValues({...baseFormValues, ...formData});
      }
    }, [propEstablishmentData, formData]);

    // Handle establishment type selection
    const handleEstablishmentTypeChange = (type: string, checked: boolean) => {
      if (propHandleEstablishmentTypeChange) {
        propHandleEstablishmentTypeChange(type, checked);
        return;
      }
      
      // Start with existing formValues
      const updatedFormValues: EstablishmentFormData = { ...formValues };
      
      // Ensure we have the correct structure for this type
      if (!updatedFormValues[type] || typeof updatedFormValues[type] !== 'object') {
        // Initialize with appropriate structure based on type
        updatedFormValues[type] = type === 'Special Event' ? 
          { 
            status: false,
            eventParticipationType: '',
            eventAddress: '',
            eventDateFrom: '',
            eventDateTo: '',
            rainDateFrom: '',
            rainDateTo: ''
          } as SpecialEventData : 
          {
            [type]: false, 
            id: null,
            size: '', 
            fee: null
          } as EstablishmentTypeData;
      }
      
      if (type === 'Special Event') {
        // For Special Event, update the status property
        const specialEventData = updatedFormValues[type] as SpecialEventData;
        updatedFormValues[type] = {
          ...specialEventData,
          status: checked
        } as SpecialEventData;
      } else {
        // For regular establishment types
        const typeData = updatedFormValues[type] as EstablishmentTypeData;
        const newTypeData: EstablishmentTypeData = {
          ...typeData,
          [type]: checked
        };
        
        // Reset the size selection if unchecked
        if (!checked) {
          newTypeData.id = null;
          newTypeData.size = '';
          newTypeData.fee = null;
        }
        
        updatedFormValues[type] = newTypeData;
      }
      
      setFormValues(updatedFormValues);
      dispatch(updateFormData({ EstablishmentType: updatedFormValues }));
      
      if (setFieldValue) {
        setFieldValue('EstablishmentType', updatedFormValues);
      }
    };
    
    // Handle size selection
    const handleSizeSelection = (type: string, sizeId: number, sizeName: string, fee: number) => {
      if (propHandleSizeSelection) {
        propHandleSizeSelection(type, sizeId, sizeName, fee);
        return;
      }
      
      // Safety check - ensure the type exists
      if (!formValues[type]) {
        return;
      }
      
      const typeData = formValues[type] as EstablishmentTypeData;
      const newTypeData: EstablishmentTypeData = {
        ...typeData,
        id: sizeId,
        size: sizeName,
        fee: fee
      };
      
      const updatedFormValues: EstablishmentFormData = { 
        ...formValues,
        [type]: newTypeData
      };
      
      setFormValues(updatedFormValues);
      dispatch(updateFormData({ EstablishmentType: updatedFormValues }));
      
      if (setFieldValue) {
        setFieldValue('EstablishmentType', updatedFormValues);
      }
    };
    
    // Handle special event field changes
    const handleSpecialEventChange = (fieldName: string, value: unknown) => {
      if (propHandleSpecialEventChange) {
        propHandleSpecialEventChange(fieldName, value);
        return;
      }
      
      // Safety check - ensure Special Event exists
      if (!formValues['Special Event']) {
        return;
      }
      
      const specialEventData = formValues['Special Event'] as SpecialEventData;
      const updatedFormValues: EstablishmentFormData = {
        ...formValues,
        'Special Event': {
          ...specialEventData,
          [fieldName]: value
        } as SpecialEventData
      };
      
      setFormValues(updatedFormValues);
      dispatch(updateFormData({ EstablishmentType: updatedFormValues }));
      
      if (setFieldValue) {
        setFieldValue('EstablishmentType', updatedFormValues);
      }
    };

    // Generate fields for the special event section
    const getSpecialEventFields = (): Fields[] => {
      // Safety check - ensure Special Event exists
      const specialEventData = formValues['Special Event'] as SpecialEventData || {
        status: false,
        eventParticipationType: '',
        eventAddress: '',
        eventDateFrom: '',
        eventDateTo: '',
        rainDateFrom: '',
        rainDateTo: ''
      };
      
      return [
        {
          fieldName: 'eventParticipationType',
          initialValue: specialEventData.eventParticipationType || '',
          fieldLabel: 'Event Participation Type',
          fieldType: 'select',
          heading: 'Special Event Details',
          options: ['Participating', 'Organizer'],
          //required: true,
          dependency: (val: unknown) => handleSpecialEventChange('eventParticipationType', val)
        },
        {
          fieldName: 'eventAddress',
          initialValue: specialEventData.eventAddress || '',
          fieldLabel: 'Event Address',
          fieldType: 'text',
          //required: true,
          dependency: (val: unknown) => handleSpecialEventChange('eventAddress', val)
        },
        {
          fieldName: 'eventDateFrom',
          initialValue: specialEventData.eventDateFrom || '',
          fieldLabel: 'Event Date From',
          fieldType: 'date',
          heading: 'Event Date',
          //required: true,
          dependency: (val: unknown) => handleSpecialEventChange('eventDateFrom', val)
        },
        {
          fieldName: 'eventDateTo',
          initialValue: specialEventData.eventDateTo || '',
          fieldLabel: 'Event Date To',
          fieldType: 'date',
          //required: true,
          dependency: (val: unknown) => handleSpecialEventChange('eventDateTo', val)
        },
        {
          fieldName: 'rainDateFrom',
          initialValue: specialEventData.rainDateFrom || '',
          fieldLabel: 'Rain Date From (if applicable)',
          fieldType: 'date',
          heading: 'Rain Date',
          dependency: (val: unknown) => handleSpecialEventChange('rainDateFrom', val)
        },
        {
          fieldName: 'rainDateTo',
          initialValue: specialEventData.rainDateTo || '',
          fieldLabel: 'Rain Date To (if applicable)',
          fieldType: 'date',
          dependency: (val: unknown) => handleSpecialEventChange('rainDateTo', val)
        }
      ];
    };

    // When component mounts or updates, make sure Formik has the current values
    useEffect(() => {
      if (setFieldValue) {
        // Update all relevant Formik fields
        setFieldValue('EstablishmentType', formValues);
      }
    }, [formValues, setFieldValue]);

    // Generate the custom form content for establishment types
    const establishmentTypeContent = (
      <Box sx={{ mt: 3 }}>
        <FormGroup>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
            Select all establishment types that apply:
          </Typography>

          {/* This section is critical - we explicitly render checkboxes for each establishment type */}
          {Object.keys(feeStructure).map((type) => {
            // Get the appropriate data structure for this type
            const typeData = formValues[type];
            
            // Determine if the type is checked
            let isChecked = false;
            if (type === 'Special Event') {
              isChecked = typeData ? (typeData as SpecialEventData).status : false;
            } else {
              isChecked = typeData ? !!(typeData as EstablishmentTypeData)[type] : false;
            }
            
            return (
              <FormControlLabel
                key={type}
                control={
                  <Checkbox
                    checked={isChecked}
                    onChange={(e) => handleEstablishmentTypeChange(type, e.target.checked)}
                    value={type}
                    name={`EstablishmentType-${type}`}
                    color="primary"
                  />
                }
                label={type}
                sx={{ mb: 1 }}
              />
            );
          })}
        </FormGroup>

        {/* Special Event fields section */}
        {formValues['Special Event'] && 
          (formValues['Special Event'] as SpecialEventData).status && (
          <Box sx={{ mt: 3, border: '1px solid #e0e0e0', padding: 2, borderRadius: 1, backgroundColor: '#f9f9f9' }}>
            <FormikForm
              allFields={getSpecialEventFields()}
              hideButtons={true}
              content={<></>}
              heading={'Special Event Details'}
              buttonPlacement={'right'}
              buttonTitle={['Cancel']}
              initialValues={formValues['Special Event']}
              enableReinitialize={true}
            />
          </Box>
        )}

        {/* Regular establishment type options */}
        {Object.keys(formValues).map((type) => {
          // Skip Special Event as it's handled above
          if (type === 'Special Event') return null;
          
          // Make sure this type exists and is selected
          if (!formValues[type]) return null;
          const typeData = formValues[type] as EstablishmentTypeData;
          if (!typeData[type]) return null;

          const options = feeStructure[type] || [];
          if (options.length === 0) return null;

          return (
            <Box key={type} sx={{ mt: 3, mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 1, color: '#2196f3' }}>
                {type} Size Options
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Please select a size option for {type.toLowerCase()}:
              </Typography>
              <TableContainer component={Paper} sx={{ boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                <Table>
                  <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableRow>
                      <TableCell>Size</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Fee</TableCell>
                      <TableCell>Select</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {options.map((option) => (
                      <TableRow key={option.id} hover>
                        <TableCell sx={{ fontWeight: 'bold' }}>{option.size}</TableCell>
                        <TableCell>{option.description}</TableCell>
                        <TableCell>${option.fee.toFixed(2)}</TableCell>
                        <TableCell>
                          <Radio
                            checked={typeData.id === option.id}
                            onChange={() => handleSizeSelection(type, option.id, option.size, option.fee)}
                            color="primary"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          );
        })}
      </Box>
    );

    // Empty fields array since we're using content
    const fields: Fields[] = [];

    return (
      <FormikForm
        innerRef={ref}
        allFields={fields}
        formMethod={handleSubmit ? (val) => handleSubmit(val as EstablishmentFormData) : (() => {})}
        heading={'Establishment Type Selection'}
        subHeading="Please select all applicable establishment types and their corresponding sizes"
        buttonTitle={['Cancel']}
        buttonPlacement={'right'}
        content={establishmentTypeContent}
        isButtonHide={isButtonHide}
        initialValues={formValues} // Pass all form values to ensure Formik has them
        enableReinitialize={true} // Re-initialize when values change
      />
    );
  }
);

EstablishmentType.displayName = 'EstablishmentType';

export default EstablishmentType;